import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Heart, List, Building2, LogIn, Info, FileText, Shield, Cookie, Users, Award, MapPin, BookOpen } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useBrand } from "../contexts/BrandContext";

export default function Header() {
  const brand = useBrand();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [venueDropdown, setVenueDropdown] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  const location = useLocation();

  // Detect if running as PWA (standalone mode)
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsPWA(isStandalone);
  }, []);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: "/browse", label: "Browse", icon: List },
    { path: "/favorites", label: "Favorites", icon: Heart },
    { path: "/guides", label: "Guides", icon: BookOpen },
    { path: "/blog", label: "Journal", icon: FileText },
    { path: "/community", label: "Community", icon: Users },
    { path: "/contributor", label: "My Profile", icon: Award },
  ];

  // Dynamic styles based on brand
  const brandStyles = {
    activeBg: brand.id === 'strider' ? 'bg-strider-teal text-white' : 'bg-blue-600 text-white',
    activeHover: brand.id === 'strider' ? 'hover:bg-teal-700' : 'hover:bg-blue-700',
    inactiveHover: brand.id === 'strider' ? 'hover:bg-teal-50 hover:text-strider-teal' : 'hover:bg-blue-50 hover:text-blue-600',
    textColor: brand.id === 'strider' ? 'text-strider-charcoal' : 'text-gray-900',
    accentText: brand.id === 'strider' ? 'text-strider-teal' : 'text-blue-600',
  };

  return (
    <>
    <header className="fixed top-0 left-0 right-0 z-[1100] bg-white/95 backdrop-blur-sm border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo with Slogan */}
          <Link 
            to="/" 
            className="flex items-center gap-4"
            data-testid="logo-link"
          >
            <img 
              src={brand.logo} 
              alt={brand.name} 
              className="h-12 w-auto rounded-sm"
              style={{ background: 'white', padding: '2px' }}
            />
            <div className="hidden sm:flex flex-col border-l border-gray-200 pl-4">
              <span className={`font-display font-bold text-4xl tracking-tight ${brandStyles.textColor}`}>
                {brand.name}
              </span>
              <span className="text-xs text-gray-500">
                {brand.tagline}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive(link.path)
                    ? brandStyles.activeBg
                    : `text-gray-600 ${brandStyles.inactiveHover}`
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                <link.icon className="w-4 h-4" />
                <span>{link.label}</span>
              </Link>
            ))}
            
            {/* For Venues Dropdown */}
            <div 
              className="relative" 
              onMouseEnter={() => setVenueDropdown(true)}
              onMouseLeave={() => setVenueDropdown(false)}
            >
              <button
                onClick={() => setVenueDropdown(!venueDropdown)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive("/claim") || isActive("/owner-login")
                    ? brandStyles.activeBg
                    : `text-gray-600 ${brandStyles.inactiveHover}`
                }`}
                data-testid="nav-for-venues"
              >
                <Building2 className="w-4 h-4" />
                <span>For Venues</span>
              </button>
              
              <AnimatePresence>
                {venueDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full right-0 mt-0 pt-2 z-50"
                  >
                    <div className="w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1">
                      <Link
                        to="/claim"
                        onClick={() => setVenueDropdown(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Building2 className="w-4 h-4" />
                        Claim Your Venue
                      </Link>
                      <Link
                        to="/owner-login"
                        onClick={() => setVenueDropdown(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <LogIn className="w-4 h-4" />
                        Owner Login
                      </Link>
                      <div className="border-t border-gray-100 my-1" />
                      <Link
                        to="/add-location"
                        onClick={() => setVenueDropdown(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <List className="w-4 h-4" />
                        Add a Location
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </nav>

          {/* Mobile Menu Button - Teal background for visibility */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 transition-colors shadow-sm"
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-5 h-5 text-white" />
            ) : (
              <Menu className="w-5 h-5 text-white" />
            )}
          </button>
        </div>
      </div>
    </header>

      {/* Mobile Menu - Outside header to avoid clipping */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden bg-white border-b border-gray-200 fixed left-0 right-0 top-16 z-[1200] shadow-lg max-h-[calc(100vh-64px)] overflow-y-auto"
          >
            <nav className="px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    isActive(link.path)
                      ? "bg-brand text-white"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <link.icon className="w-5 h-5" />
                  <span className="font-medium">{link.label}</span>
                </Link>
              ))}
              <Link
                to="/claim"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  isActive("/claim") ? "bg-brand text-white" : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <Building2 className="w-5 h-5" />
                <span className="font-medium">Claim Your Venue</span>
              </Link>
              <Link
                to="/owner-login"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  isActive("/owner-login") ? "bg-brand text-white" : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <LogIn className="w-5 h-5" />
                <span className="font-medium">Owner Login</span>
              </Link>

              {/* Legal & Info Links - Only show in browser, not PWA */}
              {!isPWA && (
                <>
                  <div className="border-t border-gray-100 my-2" />
                  <Link
                    to="/about"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive("/about") ? "bg-brand text-white" : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <Info className="w-5 h-5" />
                    <span className="font-medium">About Us</span>
                  </Link>
                  <Link
                    to="/add-location"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive("/add-location") ? "bg-brand text-white" : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <MapPin className="w-5 h-5" />
                    <span className="font-medium">Add a Location</span>
                  </Link>
                  <Link
                    to="/community"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive("/community") ? "bg-brand text-white" : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <Users className="w-5 h-5" />
                    <span className="font-medium">Community</span>
                  </Link>
                  <Link
                    to="/contributor"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive("/contributor") ? "bg-brand text-white" : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <Award className="w-5 h-5" />
                    <span className="font-medium">My Contributions</span>
                  </Link>
                  <Link
                    to="/terms"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive("/terms") ? "bg-brand text-white" : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <FileText className="w-5 h-5" />
                    <span className="font-medium">Terms of Service</span>
                  </Link>
                  <Link
                    to="/privacy"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive("/privacy") ? "bg-brand text-white" : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <Shield className="w-5 h-5" />
                    <span className="font-medium">Privacy Policy</span>
                  </Link>
                  <Link
                    to="/cookies"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive("/cookies") ? "bg-brand text-white" : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <Cookie className="w-5 h-5" />
                    <span className="font-medium">Cookie Policy</span>
                  </Link>
                </>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
